import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Copy, Loader2, Tag, CalendarClock, Flag } from 'lucide-react';
import { byOffset, durationLine, offsetLine } from '../../utils/careProgrammes';

// One programme TEMPLATE as a card: its name, what the start date is called,
// how long it runs, and its stops in timeline order (when each falls and its
// window — a stop carries no check set). The actions are the viewer's:
// edit / delete on what they own, copy on the hospital's. `copyLabel` names
// where a copy goes (the admin copies into a doctor's programmes).
function ProgrammeTemplateCard({ programme, onEdit, onDelete, onCopy, copyLabel = null, busy = false, showMeantFor = false }) {
    const { t } = useTranslation();
    const p = programme;
    const milestones = byOffset(p.milestones);
    return (
        <article data-testid={`programme-card-${p.programmeId}`} className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-colors hover:border-[#4ABDBD]/40">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h4 data-testid={`programme-card-name-${p.programmeId}`} className="truncate text-sm font-bold text-gray-800">{p.name}</h4>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-400">
                        <span className="inline-flex items-center gap-1">
                            <CalendarClock className="h-3 w-3" aria-hidden="true" />
                            {t('programmes.start_label_badge', { label: p.startLabel })}
                        </span>
                        <span data-testid={`programme-card-duration-${p.programmeId}`}>{durationLine(p.durationDays, t)}</span>
                        {p.version > 1 ? <span>{t('programmes.badge_version', { version: p.version })}</span> : null}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    {onCopy && (
                        <button
                            type="button"
                            data-testid={`programme-copy-${p.programmeId}`}
                            onClick={() => onCopy(p)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                            {copyLabel || t('programmes.copy')}
                        </button>
                    )}
                    {onEdit && (
                        <button
                            type="button"
                            data-testid={`programme-edit-${p.programmeId}`}
                            onClick={() => onEdit(p)}
                            disabled={busy}
                            aria-label={t('programmes.edit')}
                            title={t('programmes.edit')}
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-[#2D8A8A] disabled:opacity-40"
                        >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            data-testid={`programme-delete-${p.programmeId}`}
                            onClick={() => onDelete(p)}
                            disabled={busy}
                            aria-label={t('programmes.delete')}
                            title={t('programmes.delete')}
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin text-red-500" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                        </button>
                    )}
                </div>
            </div>

            <ol data-testid={`programme-card-milestones-${p.programmeId}`} className="relative flex flex-col gap-1.5 border-l-2 border-[#4ABDBD]/25 pl-3">
                {milestones.map((m) => (
                    <li key={m.key} className="relative text-[11px] text-gray-600">
                        <span className="absolute -left-[17px] top-1 h-2 w-2 rounded-full bg-[#4ABDBD]" aria-hidden="true" />
                        <span className="font-semibold text-gray-700">{m.name}</span>
                        <span className="text-gray-400"> · {offsetLine(m, t)}</span>
                    </li>
                ))}
            </ol>

            <div className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                    <Flag className="h-3 w-3" aria-hidden="true" />
                    {t('programmes.milestones_count', { n: milestones.length })}
                </span>
                {showMeantFor && p.meantFor && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#D7A05C]/15 px-2 py-0.5 text-[10px] font-semibold text-[#9a6a2c]">
                        <Tag className="h-3 w-3" aria-hidden="true" />
                        {t('programmes.badge_meant_for', { label: p.meantFor })}
                    </span>
                )}
            </div>
        </article>
    );
}

export default ProgrammeTemplateCard;
